import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../../HandleAPICalls/actions";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditRuleComponent from "./component";
import Sidebar from "../../../../Navigation/Sidebar/Sidebar";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const EditRuleContainer = (props) => {
  const [loading, setLoading] = useState(false);
  const [corporateId, setCorporateId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [departmentName, setDepartmentName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Rule data
  const [resetEnabled, setResetEnabled] = useState(false);
  const [carryForwardEnabled, setCarryForwardEnabled] = useState(false);
  const [resetConfig, setResetConfig] = useState({
    amount: 0,
    frequency: "MONTHLY",
    day: 2,
  });
  const [carryForwardConfig, setCarryForwardConfig] = useState({
    amount: 0,
    lowBalance: 0,
    frequency: "MONTHLY",
    day: 2,
  });
  
  // Store existing rule data
  const [existingRuleData, setExistingRuleData] = useState(null);

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get IDs from URL params
  useEffect(() => {
    if (params.corporateId && params.departmentId) {
      const corpId = parseInt(params.corporateId);
      const deptId = parseInt(params.departmentId);
      
      setCorporateId(corpId);
      setDepartmentId(deptId);
      
      // Get department name from location state if available
      if (location.state && location.state.departmentName) {
        setDepartmentName(location.state.departmentName);
      }
      
      fetchExistingRule(corpId, deptId);
    } else {
      toast.error("Missing parameters in URL");
      setShowForm(true);
      setIsLoading(false);
    }
  }, [params, location]);

  // Fetch existing rule data using corporateId and departmentId
  const fetchExistingRule = (corpId, deptId) => {
    setIsLoading(true);
    setApiError(false);
    
    props.getDataFromAPI(
      `/partner/api/v1/recharge-rules/${corpId}/${deptId}`,
      "GET",
      undefined,
      (response) => {
        setIsLoading(false);
        setShowForm(true);
        console.log("API Response for department:", response);
        
        if (response && response) {
          const ruleData = response;
          setExistingRuleData(ruleData);
          
          // Set reset enabled/disabled based on API response
          if (ruleData.reset) {
            setResetEnabled(true);
            setResetConfig({
              amount: ruleData.reset.amount || 0,
              frequency: ruleData.reset.frequency || "MONTHLY",
              day: ruleData.reset.day || (ruleData.reset.frequency === "DAILY" ? null : 2),
            });
          } else {
            setResetEnabled(false);
          }
          
          // Set carry forward enabled/disabled based on API response
          if (ruleData.carryForward) {
            setCarryForwardEnabled(true);
            setCarryForwardConfig({
              amount: ruleData.carryForward.amount || 0,
              lowBalance: ruleData.carryForward.lowBalance || 0,
              frequency: ruleData.carryForward.frequency || "MONTHLY",
              day: ruleData.carryForward.day || (ruleData.carryForward.frequency === "DAILY" ? null : 2),
            });
          } else {
            setCarryForwardEnabled(false);
          }
          
          toast.success("Rule data loaded successfully");
        } else {
          // No existing rule found - show empty form
          toast.info("No existing rule found. You can create new rules.");
          setResetEnabled(false);
          setCarryForwardEnabled(false);
          setApiError(false);
        }
      },
      (err) => {
        setIsLoading(false);
        setShowForm(true);
        setApiError(true);
        console.error("API Error:", err);
        toast.warning("Could not load existing rule data.");
      }
    );
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  // Handle reset config changes
  const handleResetConfigChange = (field, value) => {
    if (!resetEnabled) return;
    
    setResetConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle carry forward config changes
  const handleCarryForwardConfigChange = (field, value) => {
    if (!carryForwardEnabled) return;
    
    setCarryForwardConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle frequency changes
  const handleResetFrequencyChange = (frequency) => {
    if (!resetEnabled) return;
    
    setResetConfig(prev => ({
      ...prev,
      frequency: frequency,
      day: frequency === "DAILY" ? null : 2
    }));
  };

  const handleCarryForwardFrequencyChange = (frequency) => {
    if (!carryForwardEnabled) return;
    
    setCarryForwardConfig(prev => ({
      ...prev,
      frequency: frequency,
      day: frequency === "DAILY" ? null : 2
    }));
  };

  // Handle day/date selections
  const handleResetDayChange = (day) => {
    if (!resetEnabled) return;
    
    setResetConfig(prev => ({
      ...prev,
      day: day
    }));
  };

  const handleCarryForwardDayChange = (day) => {
    if (!carryForwardEnabled) return;
    
    setCarryForwardConfig(prev => ({
      ...prev,
      day: day
    }));
  };

  const handleResetDateSelect = (date) => {
    if (!resetEnabled) return;
    
    setResetConfig(prev => ({
      ...prev,
      day: date
    }));
  };

  const handleCarryForwardDateSelect = (date) => {
    if (!carryForwardEnabled) return;
    
    setCarryForwardConfig(prev => ({
      ...prev,
      day: date
    }));
  };

  // Validate and update rule
  const handleUpdateRule = () => {
    // Validate reset config if enabled
    if (resetEnabled) {
      if (!resetConfig.amount || resetConfig.amount <= 0) {
        toast.error("Please enter a valid recharge amount for Reset");
        return;
      }
      if (resetConfig.frequency !== "DAILY" && !resetConfig.day) {
        toast.error("Please select a day/date for Reset");
        return;
      }
    }

    // Validate carry forward config if enabled
    if (carryForwardEnabled) {
      if (!carryForwardConfig.amount || carryForwardConfig.amount <= 0) {
        toast.error("Please enter a valid recharge amount for Carry Forward");
        return;
      }
      if (carryForwardConfig.lowBalance < 0) {
        toast.error("Low balance limit cannot be negative");
        return;
      }
      if (carryForwardConfig.frequency !== "DAILY" && !carryForwardConfig.day) {
        toast.error("Please select a day/date for Carry Forward");
        return;
      }
    }

    setLoading(true);
    
    // Prepare payload for PUT request
    const payload = {
      corporateId: corporateId,
      departmentId: departmentId,
      reset: resetEnabled ? {
        enabled: true,
        amount: parseFloat(resetConfig.amount),
        frequency: resetConfig.frequency,
        day: resetConfig.frequency !== "DAILY" ? parseInt(resetConfig.day) : null
      } : null,
      carryForward: carryForwardEnabled ? {
        enabled: true,
        amount: parseFloat(carryForwardConfig.amount),
        lowBalance: parseFloat(carryForwardConfig.lowBalance),
        frequency: carryForwardConfig.frequency,
        day: carryForwardConfig.frequency !== "DAILY" ? parseInt(carryForwardConfig.day) : null
      } : null
    };

    console.log("Update Payload:", payload);

    // Call update API
    props.getDataFromAPI(
      `/partner/api/v1/recharge-rules/${corporateId}/${departmentId}`,
      "PUT",
      payload,
      (response) => {
        setLoading(false);
        if (response) {
          toast.success("Rule updated successfully!");
          // Update existing rule data with new response
          navigate(-1)
          if (response) {
            setExistingRuleData(response);

          }
        } else {
          toast.error("Failed to update rule");
        }
      },
      (err) => {
        setLoading(false);
        console.error("Update failed:", err);
        toast.error(err.message || "Failed to update rule");
      }
    );
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  // Show loading only for initial load
  if (isLoading && !showForm) {
    return (
      <div className="edit-rule-layout">
        <Sidebar />
        <div className="edit-rule-content">
          <Box className="loading-container">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3, color: '#666' }}>
              Loading rule data...
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-rule-layout">
      <Sidebar />
      <div className="edit-rule-content">
        <div className="edit-rule-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handleBackClick}
              sx={{ 
                color: '#666',
                '&:hover': { 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <h2 className="edit-rule-title">Edit Department Rules</h2>
              <div className="id-display">
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Corporate ID: {corporateId} | Department ID: {departmentId}
                </Typography>
                {departmentName && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#3f51b5', fontWeight: 'bold', mt: 0.5 }}>
                    Department: {departmentName}
                  </Typography>
                )}
              </div>
            </Box>
          </Box>
        </div>
        
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <div className="edit-rule-main">
          {/* Show API error warning */}
          {apiError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Could not load existing rule data.
            </Alert>
          )}
          
          {showForm ? (
            <EditRuleComponent
              resetEnabled={resetEnabled}
              carryForwardEnabled={carryForwardEnabled}
              resetConfig={resetConfig}
              handleResetConfigChange={handleResetConfigChange}
              handleResetFrequencyChange={handleResetFrequencyChange}
              handleResetDayChange={handleResetDayChange}
              handleResetDateSelect={handleResetDateSelect}
              carryForwardConfig={carryForwardConfig}
              handleCarryForwardConfigChange={handleCarryForwardConfigChange}
              handleCarryForwardFrequencyChange={handleCarryForwardFrequencyChange}
              handleCarryForwardDayChange={handleCarryForwardDayChange}
              handleCarryForwardDateSelect={handleCarryForwardDateSelect}
              loading={loading}
              onUpdate={handleUpdateRule}
              onCancel={handleCancel}
              existingRuleData={existingRuleData}
            />
          ) : (
            <Alert severity="error">
              Failed to load the edit form. Please check the URL parameters and try again.
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    currentCorporate: state.corporate?.currentCorporate || 
                     state.auth?.currentCorporate || 
                     state.user?.currentCorporate,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(EditRuleContainer);